/**
 * Pure geometry for ImageCropper. The image cover-fits the circular viewport
 * at zoom 1 and scales up around its center from there; `offset` is the
 * image's translation (in stage px) away from being centered, clamped so the
 * viewport circle never shows past the image's edges.
 */

export interface Offset {
  x: number;
  y: number;
}

export interface CoverLayout {
  /** Rendered image width/height in stage px at the current zoom. */
  displayedWidth: number;
  displayedHeight: number;
  /** Largest |offset| on each axis that keeps the viewport inside the image. */
  maxOffsetX: number;
  maxOffsetY: number;
  /** Stage px per source px. */
  scale: number;
}

export function computeCoverLayout(
  naturalWidth: number,
  naturalHeight: number,
  viewport: number,
  zoom: number,
): CoverLayout {
  const scale = (viewport / Math.min(naturalWidth, naturalHeight)) * zoom;
  const displayedWidth = naturalWidth * scale;
  const displayedHeight = naturalHeight * scale;
  return {
    displayedWidth,
    displayedHeight,
    maxOffsetX: Math.max(0, (displayedWidth - viewport) / 2),
    maxOffsetY: Math.max(0, (displayedHeight - viewport) / 2),
    scale,
  };
}

export function clampOffset(offset: Offset, layout: CoverLayout): Offset {
  return {
    x: Math.min(layout.maxOffsetX, Math.max(-layout.maxOffsetX, offset.x)),
    y: Math.min(layout.maxOffsetY, Math.max(-layout.maxOffsetY, offset.y)),
  };
}

export interface CropRect {
  sx: number;
  sy: number;
  /** Source-px side length of the square crop region. */
  side: number;
}

/**
 * The source-image rectangle currently visible through the viewport circle.
 * Mirrors the on-screen layout exactly — what you see is what crops.
 */
export function computeCropRect(
  naturalWidth: number,
  naturalHeight: number,
  viewport: number,
  zoom: number,
  offset: Offset,
): CropRect {
  const layout = computeCoverLayout(naturalWidth, naturalHeight, viewport, zoom);
  const clamped = clampOffset(offset, layout);
  const side = viewport / layout.scale;
  // Positive offset moves the image right/down, revealing the region left/up
  // of its center — hence the subtraction.
  const sx = (naturalWidth - side) / 2 - clamped.x / layout.scale;
  const sy = (naturalHeight - side) / 2 - clamped.y / layout.scale;
  return {
    sx: Math.min(Math.max(0, sx), naturalWidth - side),
    sy: Math.min(Math.max(0, sy), naturalHeight - side),
    side,
  };
}
